import React, { useEffect, useState } from 'react'
import { discountCodeFormatter, DiscountCodeFormSchema, discountCodeSchema, DiscountType } from 'discounts/_shared'
import { FormWrapper } from 'uiComponents/form/form'
import { addYears } from 'date-fns'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import './discountCodeForm.scss'
import { ComboboxFieldFormik, MultiSelectComboboxFieldFormik } from 'uiComponents/combobox/comboboxFieldFormik'
import NumberInputFieldFormik from 'uiComponents/input/numberInput/numberInputFieldFormik'
import { useGetAllProductsForSelect, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { useGetAllDiscountPoolsForSelect, useIsLoadingDiscountPools } from '../redux'
import { DateRangeFieldFormik } from 'uiComponents/input/date'
import { Checkbox, CheckboxFieldFormik, SingleSelectFieldFormik, Toggle } from 'uiComponents/input'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import ActionButton from 'uiComponents/buttons'
import { BaseRouteParams } from 'hocs'
import { useParams } from 'react-router-dom'
import { useAddCodeByFormMutation, useEditDiscountCodeMutation } from 'discounts/reduxQuery'
import { ErrorResponse } from '../../../types'
import { DiscountCodeDetails } from 'discounts/types'
import { FormikHelpers, useFormikContext } from 'formik'
import { FormItemName } from 'uiComponents/form/formElements'
import classNames from 'classnames'
import { useCurrency } from 'uiComponents/money'
import { TooltipWrapper } from 'uiComponents/tooltip.new'
import { showErrorMessage } from 'uiComponents/message'
import { useTranslation } from 'translations'

const defaultValues = {
    rate: 'percentage',
    bookingWindow: {
        from: new Date().toISOString(),
        to: addYears(new Date(), 1).toISOString(),
    },
    eventWindow: {
        from: new Date().toISOString(),
        to: addYears(new Date(), 1).toISOString(),
    },
} as Partial<DiscountCodeFormSchema>

interface DiscountCodeFormProps {
    discountType: DiscountType
    afterSubmit?: (discount: DiscountCodeFormSchema) => void
    onCancel?: () => void
}

interface NewDiscountCodeFormProps extends DiscountCodeFormProps {
    selectedPoolUuid?: string
    discount?: never
}

interface EditDiscountCodeFormProps extends DiscountCodeFormProps {
    selectedPoolUuid?: never
    discount: Partial<DiscountCodeFormSchema>
}

const DiscountCodeFormFields = ({
    discountType,
    selectedPoolUuid,
    isNewCode,
    onCancel,
    isLoading,
}: {
    discountType: DiscountType
    selectedPoolUuid?: string
    isNewCode: boolean
    onCancel?: () => void
    isLoading: boolean
}) => {
    const { t } = useTranslation()
    const { values, setFieldValue } = useFormikContext<DiscountCodeFormSchema>()
    const products = useGetAllProductsForSelect()
    const loadingProducts = useIsLoadingCategoriesOrArticles()
    const discountPools = useGetAllDiscountPoolsForSelect()
    const loadingPools = useIsLoadingDiscountPools()
    const currencySymbol = useCurrency().symbol
    const [remainderUsableDisabledReason, setRemainderUsableDisabledReason] = useState<string | undefined>(undefined)

    const handleEnableEventWindowToggle = () => {
        const enabled = !values.enableEventWindow
        setFieldValue('enableEventWindow', enabled)
        if (enabled && !values.eventWindow) {
            setFieldValue('eventWindow', defaultValues.eventWindow)
        }
    }

    useEffect(() => {
        if (values.rate === 'percentage') {
            setRemainderUsableDisabledReason(t('discounts.shared.form.remainderNeedsFixedAmount'))
            setFieldValue('remainderUsable', false)
        } else if (!values.enableMaxUses || values.maxUses !== 1) {
            setRemainderUsableDisabledReason(t('discounts.shared.form.remainderNeedsSingleUse'))
            setFieldValue('remainderUsable', false)
        } else {
            setRemainderUsableDisabledReason(undefined)
        }
    }, [values.rate, values.enableMaxUses, values.maxUses])

    return (
        <>
            <div className="row">
                <TextInputFieldFormik
                    name="code"
                    className="column"
                    icon="Barcode"
                    placeholder="SALE25"
                    label={t('discounts.shared.form.codeLabel')}
                    whiteBackground
                    border
                />
                <ComboboxFieldFormik
                    id="poolUuid"
                    label={t('discounts.shared.form.poolLabel')}
                    icon="ScanBarcode"
                    className="column"
                    options={discountPools}
                    loading={loadingPools}
                    value={selectedPoolUuid}
                    disabled={!isNewCode}
                />
            </div>
            {discountType === 'product' && (
                <div className="row">
                    <div className="column product">
                        <MultiSelectComboboxFieldFormik
                            options={products}
                            id="products.uuids"
                            label={t('discounts.shared.form.productsLabel')}
                            loading={loadingProducts}
                        />
                    </div>
                    <div className={classNames('column product-amount', { enabled: values.enableProductAmount })}>
                        <FormItemName
                            className="column-title togglable"
                            onClick={() => {
                                setFieldValue('enableProductAmount', !values.enableProductAmount)
                            }}
                        >
                            <Toggle
                                id="product-amount-toggle"
                                title={t('discounts.shared.form.limitUsesPerProduct')}
                                className="toggle"
                                isOn={values.enableProductAmount}
                                onClick={() => {
                                    setFieldValue('enableProductAmount', !values.enableProductAmount)
                                }}
                            />
                            <div>{t('discounts.shared.form.limitUsesPerProduct')}</div>
                        </FormItemName>
                        <NumberInputFieldFormik
                            name="products.amount"
                            disabled={!values.enableProductAmount}
                            placeholder=""
                            whiteBackground
                            border
                        />
                        <div className="explanation">{t('discounts.shared.form.withinEachOrder')}</div>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="column booking-window">
                    <DateRangeFieldFormik
                        name="bookingWindow"
                        label={t('discounts.shared.form.bookingWindowLabel')}
                        lastAvailableDay={addYears(new Date(), 5)}
                        allowComparison={false}
                        openOnCurrentAndNextMonth
                    />
                    <div className="explanation">{t('discounts.shared.form.bookingWindowExplanation')}</div>
                </div>
                <div className={classNames('column event-window', { enabled: values.enableEventWindow })}>
                    <FormItemName className="column-title togglable" onClick={handleEnableEventWindowToggle}>
                        <Toggle
                            id="event-window-toggle"
                            title={t('discounts.shared.form.limitEventWindow')}
                            className="toggle"
                            isOn={values.enableEventWindow}
                            onClick={handleEnableEventWindowToggle}
                        />
                        <div>{t('discounts.shared.form.limitEventWindow')}</div>
                    </FormItemName>
                    {values.enableEventWindow ? (
                        <DateRangeFieldFormik
                            name="eventWindow"
                            lastAvailableDay={addYears(new Date(), 5)}
                            allowComparison={false}
                            openOnCurrentAndNextMonth
                        />
                    ) : (
                        <div className="event-window-placeholder" />
                    )}
                    <div className="explanation">{t('discounts.shared.form.eventWindowExplanation')}</div>
                </div>
            </div>
            <div className="row">
                <div className="column discount-value">
                    <SingleSelectFieldFormik
                        name="rate"
                        className="rate"
                        label={t('discounts.shared.form.discountValueLabel')}
                        options={[
                            { value: 'percentage', name: t('discounts.shared.form.ratePercentage') },
                            { value: 'flat', name: t('discounts.shared.form.rateFlat') },
                        ]}
                        validateOnChange={false}
                        validateOnBlur={false}
                    />
                    <NumberInputFieldFormik
                        name="reductionRate"
                        placeholder=""
                        whiteBackground
                        border
                        maxLength={6}
                        prefix={values.rate === 'flat' ? currencySymbol : ''}
                        suffix={values.rate === 'percentage' ? '%' : ''}
                    />
                </div>
                <div className={classNames('column uses', { enabled: values.enableMaxUses })}>
                    <FormItemName
                        className="column-title togglable"
                        onClick={() => {
                            setFieldValue('enableMaxUses', !values.enableMaxUses)
                        }}
                    >
                        <Toggle
                            id="max-uses-toggle"
                            title={t('discounts.shared.form.limitTotalUses')}
                            className="toggle"
                            isOn={values.enableMaxUses}
                            onClick={() => {
                                setFieldValue('enableMaxUses', !values.enableMaxUses)
                            }}
                        />
                        <div>{t('discounts.shared.form.limitTotalUses')}</div>
                    </FormItemName>
                    <NumberInputFieldFormik
                        name="maxUses"
                        disabled={!values.enableMaxUses}
                        placeholder=""
                        whiteBackground
                        border
                    />
                </div>
            </div>
            <div className="row boxed">
                <CheckboxFieldFormik variant="inline" name="remainderUsable">
                    {(props) => (
                        <TooltipWrapper
                            className={classNames('remainder-usable', { disabled: !!remainderUsableDisabledReason })}
                            text={remainderUsableDisabledReason}
                            position="bottom"
                            arrowPosition="left"
                        >
                            <Checkbox {...props} disabled={!!remainderUsableDisabledReason} />
                            <div className="text">
                                <div className="title">{t('discounts.shared.form.remainderTitle')}</div>
                                <div className="explanation">{t('discounts.shared.form.remainderExplanation')}</div>
                            </div>
                        </TooltipWrapper>
                    )}
                </CheckboxFieldFormik>
            </div>
            <div className="row actions">
                {onCancel && (
                    <CancelButton className="back-button" secondary size="large" onClick={onCancel}>
                        {isNewCode ? t('common.back') : t('common.cancel')}
                    </CancelButton>
                )}
                <ActionButton size="large" type="submit" kind="default" disabled={isLoading}>
                    {isNewCode ? t('discounts.shared.form.createCode') : t('discounts.shared.form.saveCode')}
                </ActionButton>
            </div>
        </>
    )
}

export const DiscountCodeForm = ({
    discountType,
    selectedPoolUuid,
    discount,
    onCancel,
    afterSubmit,
}: NewDiscountCodeFormProps | EditDiscountCodeFormProps) => {
    const isNewCode = !discount
    const routeParams = useParams<BaseRouteParams>()
    const [submitForm, { isLoading }] = isNewCode ? useAddCodeByFormMutation() : useEditDiscountCodeMutation()

    const onSubmit = async (
        values: DiscountCodeFormSchema,
        { setFieldError }: FormikHelpers<DiscountCodeFormSchema>,
    ) => {
        const formattedFormData = discountCodeFormatter.toDomain(values)
        const dataToSubmit = {
            accountSlug: routeParams.accountSlug,
            formData: formattedFormData,
            poolUuid: values.poolUuid!,
        } as any

        if (!isNewCode) {
            dataToSubmit.codeUuid = values.id!
        }

        submitForm(dataToSubmit)
            .unwrap()
            .then((data: DiscountCodeDetails) => {
                return afterSubmit?.(values)
            })
            .catch((data: ErrorResponse<DiscountCodeDetails>) => {
                if (data.error.code === 'coupon_already_exists') {
                    setFieldError('code', data.error.message)
                } else if (data.error.message) {
                    showErrorMessage(data.error.message)
                } else if (data.error.errors) {
                    showErrorMessage(JSON.stringify(data.error.errors))
                }
            })
    }

    return (
        <FormWrapper
            className="discount-code-form modal-body"
            validationSchema={discountCodeSchema}
            initialValues={discount ?? { ...defaultValues, type: discountType, poolUuid: selectedPoolUuid }}
            onSubmit={onSubmit}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <DiscountCodeFormFields
                discountType={discountType}
                selectedPoolUuid={selectedPoolUuid}
                isNewCode={isNewCode}
                onCancel={onCancel}
                isLoading={isLoading}
            />
        </FormWrapper>
    )
}
